// RenderProductList
import React, { useState, useEffect, useMemo } from 'react';
import { generatePublicUrl } from '../../../urlConfig';
import Loader from '../../../components/Loader';
import { Button } from '@material-ui/core';
import PicturesViews from '../../../components/Pictures/PicturesViews';
import {
    addNewProductFeaturedProductItem,
    removeProductByIdProductFeaturedProductItem,
    updateFeaturedProductOrder
} from '../../../actions';
import { useDispatch } from 'react-redux';
import ButtonBox from './ButtonBox';
import IconItemProductBox from './IconItemProductBox';
import RenderTextEmptyProduct from '../../../components/RenderTextEmptyProduct';

const getId = (value) => {
    if (!value) {
        return "";
    }

    if (typeof value === "object") {
        return String(value._id || value.id || "");
    }

    return String(value);
};

const isSameId = (left, right) => {
    const leftId = getId(left);
    const rightId = getId(right);

    return leftId && rightId && leftId === rightId;
};

const getProductObj = (item) => {
    if (!item) {
        return {};
    }

    if (item.product && typeof item.product === "object") {
        return item.product;
    }

    if (item.productId && typeof item.productId === "object") {
        return item.productId;
    }

    return {};
};

const getProductPictures = (item) => {
    const product = getProductObj(item);

    if (Array.isArray(product.productPictures)) {
        return product.productPictures.filter((picture) => picture?.img);
    }

    return [];
};

const getSelectedPicture = (item) => {
    if (item?.selectedPicture?.img) {
        return item.selectedPicture;
    }

    const pictures = getProductPictures(item);
    const selectedPictureId = getId(item?.selectedPictureId);

    if (selectedPictureId) {
        const byId = pictures.find((picture) => isSameId(picture._id, selectedPictureId));

        if (byId) {
            return byId;
        }
    }

    if (item?.img) {
        const byImg = pictures.find((picture) => picture.img === item.img);

        if (byImg) {
            return byImg;
        }
    }

    const mainPicture = pictures.find((picture) => picture.mainImg);

    if (mainPicture) {
        return mainPicture;
    }

    return pictures[0] || null;
};

const getPreviewImage = (item) => {
    const selectedPicture = getSelectedPicture(item);
    const product = getProductObj(item);

    return selectedPicture?.img || item?.img || product?.reducedImage || "";
};

const RenderProductList = ({
    hideBackButton,
    filterProduct,
    itemObj,
    setTypeModal,
    products,
    productIds,
    setSelectedProduct,
    setProductIds,
    selectedProduct,
    setFilterProductId,
    featuredProductItemObj,
    filterProductId,
    setUpdatedProducts,
    updatedProducts
}) => {
    const [showSelectProduct, setShowSelectProduct] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEditOrderInput, setShowEditOrderInput] = useState(false);
    const [numberEditOrderInput, setNumberEditOrderInput] = useState(null);
    const [editOrderId, setEditOrderId] = useState(null);

    const dispatch = useDispatch();

    const currentProducts = Array.isArray(featuredProductItemObj?.products)
        ? featuredProductItemObj.products
        : [];

    const uniqueProductIds = useMemo(() => {
        return [...new Set(currentProducts.map((product) => getId(product.productId)))];
    }, [currentProducts]);

    useEffect(() => {
        if (products.length > 0 && filterProductId.length === 0) {
            const productArray = products.filter((product) => !uniqueProductIds.includes(product._id));
            setFilterProductId(productArray);
        }
    }, [products, filterProductId, uniqueProductIds, setFilterProductId]);

    if (!itemObj) return <Loader />;

    const getPreviewProductItem = (item) => {
        const updatedItem = updatedProducts.find((product) => product._id === item._id);

        if (!updatedItem) {
            return item;
        }

        return {
            ...item,
            ...updatedItem
        };
    };

    const addOrUpdateChangedProduct = (nextProduct) => {
        setUpdatedProducts((prevState) => {
            const exists = prevState.find((item) => item._id === nextProduct._id);

            if (exists) {
                return prevState.map((item) => (
                    item._id === nextProduct._id ? nextProduct : item
                ));
            }

            return [...prevState, nextProduct];
        });
    };

    const handleSelectProductPicture = (item, picture) => {
        if (!picture?._id) {
            return;
        }

        const nextProduct = {
            ...item,
            selectedPictureId: picture._id,
            selectedPicture: picture,
            order: Number(item.order || 0)
        };

        addOrUpdateChangedProduct(nextProduct);
    };

    const submitAddNewProduct = () => {
        const payload = {
            id: featuredProductItemObj._id,
            newProducts: productIds
        };

        if (payload.newProducts.length > 0) {
            dispatch(addNewProductFeaturedProductItem(payload));
            setProductIds([]);
            setShowSelectProduct(false);
            setFilterProductId([]);
        }
    };

    const confirmDelete = () => {
        const payload = {
            productId: editOrderId,
            id: featuredProductItemObj._id
        };

        dispatch(removeProductByIdProductFeaturedProductItem(payload));
        setShowDeleteConfirm(false);
        setEditOrderId(null);
        setFilterProductId([]);
    };

    const handleShowEditOrder = (item) => {
        setEditOrderId(item._id);
        setNumberEditOrderInput(item.order);
        setShowEditOrderInput(!showEditOrderInput);

        if (numberEditOrderInput !== item.order) {
            const nextProduct = {
                ...item,
                order: Number(numberEditOrderInput || 0),
                selectedPictureId: item.selectedPictureId || getSelectedPicture(item)?._id || null
            };

            addOrUpdateChangedProduct(nextProduct);
        } else {
            setUpdatedProducts((prevState) => (
                prevState.filter((updatedItem) => updatedItem._id !== item._id)
            ));
        }
    };

    const confirmUpdateProductConfirm = () => {
        const payload = {
            id: featuredProductItemObj._id,
            products: updatedProducts.map((item) => ({
                _id: item._id,
                productId: getId(item.productId),
                selectedPictureId: item.selectedPictureId || getSelectedPicture(item)?._id || null,
                order: Number(item.order || 0)
            }))
        };

        if (payload.products.length > 0) {
            dispatch(updateFeaturedProductOrder(payload))
                .then(() => setUpdatedProducts([]));
        }
    };

    return (
        <div className="renderProductMiniList">
            {!hideBackButton && (
                <Button onClick={() => setTypeModal("details")}>
                    Назад
                </Button>
            )}

            <div className="renderProductMiniList__row">
                {currentProducts.length > 0
                    ? currentProducts.map((item) => {
                        const previewItem = getPreviewProductItem(item);
                        const product = getProductObj(item);
                        const pictures = getProductPictures(item);
                        const currentImage = getPreviewImage(previewItem);
                        const currentPictureId = getId(previewItem.selectedPictureId || getSelectedPicture(previewItem)?._id);

                        return (
                            <div
                                className="renderProductMiniList__item"
                                key={item._id}
                            >
                                <div className="renderProductMiniList__imgBox">
                                    {currentImage ? (
                                        <img src={generatePublicUrl(currentImage)} alt="Фото" />
                                    ) : (
                                        <div className="renderProductMiniList__noImg">
                                            Нет фото
                                        </div>
                                    )}
                                </div>

                                <div className="renderProductMiniList__info">
                                    <strong>
                                        {product.fullName || product.name || "Товар"}
                                    </strong>

                                    <span>
                                        {product.categoryName || "Без категории"}
                                    </span>
                                </div>

                                {pictures.length > 0 && (
                                    <div className="renderProductMiniList__picturePicker">
                                        {pictures.map((picture) => {
                                            const isActive = isSameId(currentPictureId, picture._id);

                                            return (
                                                <button
                                                    type="button"
                                                    key={picture._id || picture.img}
                                                    className={isActive ? "active" : ""}
                                                    onClick={() => handleSelectProductPicture(item, picture)}
                                                    title="Выбрать это фото"
                                                >
                                                    <img
                                                        src={generatePublicUrl(picture.img)}
                                                        alt=""
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {showEditOrderInput && editOrderId === item._id ? (
                                    <input
                                        className="renderProductMiniList__inputOrder"
                                        type="text"
                                        value={numberEditOrderInput}
                                        onChange={(e) => setNumberEditOrderInput(e.target.value)}
                                    />
                                ) : (
                                    <PicturesViews
                                        type="product"
                                        right="10px"
                                        picture={previewItem}
                                    />
                                )}

                                <IconItemProductBox
                                    setEditOrderId={setEditOrderId}
                                    setShowEditOrderInput={setShowEditOrderInput}
                                    setShowDeleteConfirm={setShowDeleteConfirm}
                                    handleShowEditOrder={handleShowEditOrder}
                                    setUpdatedProducts={setUpdatedProducts}
                                    showEditOrderInput={showEditOrderInput}
                                    showDeleteConfirm={showDeleteConfirm}
                                    updatedProducts={updatedProducts}
                                    confirmDelete={confirmDelete}
                                    editOrderId={editOrderId}
                                    item={item}
                                />
                            </div>
                        );
                    })
                    : <RenderTextEmptyProduct />}
            </div>

            {updatedProducts.length > 0 && (
                <div className="renderProductMiniList__savePanel">
                    <span>
                        Есть несохранённые изменения: {updatedProducts.length}
                    </span>

                    <button
                        type="button"
                        onClick={confirmUpdateProductConfirm}
                    >
                        Сохранить изменения
                    </button>
                </div>
            )}

            <ButtonBox
                filterProduct={filterProduct}
                productIdsLength={productIds.length}
                confirmUpdateProductConfirm={confirmUpdateProductConfirm}
                setShowSelectProduct={setShowSelectProduct}
                submitAddNewProduct={submitAddNewProduct}
                showEditOrderInput={showEditOrderInput}
                setSelectedProduct={setSelectedProduct}
                showSelectProduct={showSelectProduct}
                updatedProducts={updatedProducts}
                selectedProduct={selectedProduct}
                setProductIds={setProductIds}
                productIds={productIds}
                products={products}
            />
        </div>
    );
};

export default RenderProductList;